import * as React from 'react';

import { ErrorText } from '../ErrorText';
import { Form } from '../Form';
import defaultMessages from '../i18n/defaultMessages';
import { Input } from '../Input';
import { LoadingDots } from '../Loaders/Dots';
import { PrimaryButton } from '../PrimaryButton';
import { GlobalProps } from '../types';
import { classes, debounce } from '../utils';
import { useTranslateMessage } from '../utils/translate-messages';

import styles from './styles.module.css';

const SUBMIT_DEBOUNCE_IN_MS = 500;

// Allow multi-digit autofill/paste handling; short grace avoids flashing incomplete error
const INJECTION_GRACE_MS = 800;

enum Key {
  BackSpace = 'Backspace',
  ArrowLeft = 'ArrowLeft',
  ArrowRight = 'ArrowRight',
  Enter = 'Enter',
}

export type Props = GlobalProps & {
  length?: 6 | 8;
  autoSubmit?: boolean;
  buttonText?: string;
  center?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  loading?: boolean;
  showError?: boolean;
  onFormSubmit?: (value: string) => void;
  variant?: 'default' | 'dark';
};

export type CodeInputHandle = {
  clearForm: () => void;
  tagName: string;
  element: HTMLFormElement | null;
};

export const CodeInputForm = React.forwardRef<CodeInputHandle, Props>(
  function CodeInputFormComponent(props, ref) {
    const {
      length = 6,
      onFormSubmit,
      autoSubmit = true,
      buttonText = '',
      center = true,
      disabled = false,
      errorMessage = '',
      loading = false,
      name = 'code-input-form',
      showError = false,
      variant = 'default',
    } = props;

    const translateMessage = useTranslateMessage();

    const translatedInvalidInput = translateMessage({
      id: 'media-ui-core_onlyEnterNumbers',
      defaultMessage: defaultMessages['media-ui-core_onlyEnterNumbers'],
    });
    const translatedFormNotComplete = translateMessage({
      id: 'media-ui-core_fillOutAllFields',
      defaultMessage: defaultMessages['media-ui-core_fillOutAllFields'],
    });

    const formRef = React.useRef<HTMLFormElement | null>(null);
    // create an array of length
    const initialState = React.useMemo(() => {
      return new Array(length).fill('');
    }, [length]);

    const [value, setValue] = React.useState(initialState);
    const [error, setError] = React.useState(errorMessage);
    const [valueChanged, setValueChanged] = React.useState(false);

    // Timestamp when full code dropped in (autofill/paste)
    const fullInjectionAt = React.useRef<number>(0);

    const onSubmit = React.useCallback(
      debounce((e?: React.FormEvent<HTMLFormElement>) => {
        if (e) {
          e.preventDefault();
        }

        const inputValue = value.join('');

        if (inputValue.length === length) {
          if (onFormSubmit) {
            setValueChanged(false);
            onFormSubmit(inputValue);
          }

          return;
        }

        // Grace window: delay incomplete error after autofill/paste so user doesn't see a flash.
        // After the window ends we show the incomplete error (ignores showError prop).
        const withinGrace =
          Date.now() - fullInjectionAt.current < INJECTION_GRACE_MS;

        if (!withinGrace) {
          setError(translatedFormNotComplete);
        }
      }, SUBMIT_DEBOUNCE_IN_MS),
      [value, length, onFormSubmit, showError]
    );

    const setFocusByIndex = React.useCallback(
      (index: number): boolean => {
        const nextFocusSibling = document.querySelector(
          `input[name=${name}-field-${index}]`
        ) as HTMLInputElement;

        if (nextFocusSibling) {
          nextFocusSibling.focus();

          return true;
        }

        return false;
      },
      [name]
    );

    const onChange = React.useCallback(
      (index: number) => {
        return function changeHandler(e: React.ChangeEvent<HTMLInputElement>) {
          const targetValue = e.target.value;
          const numberRegex = /[0-9]$/;

          // First input received multiple digits (autofill/paste)
          if (index === 0 && targetValue.length > 1) {
            fullInjectionAt.current = Date.now();
            // Strip non-digits
            const sanitized = targetValue.replace(/\D/g, '');
            const digits = sanitized.slice(0, length).split('');

            // Fill all inputs
            setValue(digits);

            // Decide: submit now or move focus
            if (digits.length === length) {
              // Got full code; submit now if autoSubmit
              setError('');
              setValueChanged(true);
              if (autoSubmit) {
                // Call handler directly, no debounce wait
                if (onFormSubmit) {
                  onFormSubmit(digits.join(''));
                  setValueChanged(false);
                } else {
                  // Else use debounced submit
                  setTimeout(() => {
                    onSubmit();
                  }, 0);
                }
              } else if (onFormSubmit) {
                // AutoSubmit off: let user press button
              }
            } else {
              // Partial: move focus forward
              setFocusByIndex(digits.length);
            }

            // Done with multi-digit path
            return;
          }

          // Single-digit typing
          if (!targetValue.match(numberRegex) && targetValue !== '') {
            setError(translatedInvalidInput);

            return;
          }

          setValue((prev) =>
            prev.map((v, i) => (i === index ? targetValue : v))
          );

          // if valid input was entered, increment focus
          if (targetValue !== '') {
            setFocusByIndex(index + 1);
          }

          setValueChanged(true);
        };
      },
      [autoSubmit, length, name, setFocusByIndex, setValueChanged, onSubmit]
    );

    const onKeyDown = React.useCallback(
      (index: number) => {
        return function handler(e: React.KeyboardEvent<HTMLInputElement>) {
          setError('');

          const targetValue = (e.target as HTMLInputElement).value;

          if (e.key === Key.BackSpace && targetValue === '') {
            setValueChanged(true);
            setFocusByIndex(index - 1);
          }

          // handle enter command to submit
          if (!buttonText && e.key === Key.Enter) {
            onSubmit();
          }
        };
      },
      [name, buttonText, setFocusByIndex, onSubmit, setValueChanged]
    );

    // allow arrow right + left actions
    const onKeyUp = React.useCallback(
      (index: number) => {
        return function handler(e: React.KeyboardEvent<HTMLInputElement>) {
          const { key } = e;

          if (key === Key.ArrowLeft) {
            setFocusByIndex(index - 1);
          }

          if (key === Key.ArrowRight) {
            setFocusByIndex(index + 1);
          }
        };
      },
      [setFocusByIndex]
    );

    const onPaste = React.useCallback(
      (index: number) => {
        return function handler(e: React.ClipboardEvent<HTMLInputElement>) {
          e.preventDefault();
          const pasteInput = (e.clipboardData.getData('text') as string)
            .split('')
            .filter((input, i) => !(input === '-' && i === length / 2));

          for (let i = 0; i < pasteInput.length; i += 1) {
            const input = pasteInput[i];
            const numberRegex = /[0-9]$/;

            if (!input?.match(numberRegex)) {
              setError(translatedInvalidInput);
              break;
            }

            const nativeInputValueSetter = (
              Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype,
                'value'
              ) as PropertyDescriptor
            ).set;

            const element = document.querySelector(
              `input[name=${name}-field-${index + i}]`
            ) as HTMLInputElement;

            // nativeInputValueSetter may be undefined
            (nativeInputValueSetter as (v: string) => void).call(
              element,
              input
            );
            const event = new Event('input', {
              bubbles: true,
              cancelable: true,
            });

            element.dispatchEvent(event);
            setValueChanged(true);
            setError('');
          }
        };
      },
      [setValueChanged]
    );

    const clearFormFunc = React.useCallback(() => {
      setValue(initialState);
      setFocusByIndex(0);
      setValueChanged(true);
    }, []);

    // add a form clear method to the ref
    React.useImperativeHandle(ref, () => ({
      clearForm() {
        clearFormFunc();
      },
      tagName: 'FORM',
      element: formRef.current,
    }));

    const halfForm = React.useCallback(
      (len: number, left: boolean) => {
        const inputArray = [];
        const startIndex = left ? 0 : len / 2;

        for (let i = startIndex; i < len / 2 + startIndex; i += 1) {
          inputArray.push(
            <Input
              id={`input-${i}`}
              inputProps={{
                autoFocus: i === 0,
                type: 'tel',
                onKeyDown: onKeyDown(i),
                onKeyUp: onKeyUp(i),
                onPaste: onPaste(i),
                pattern: '[0-9]?',
                inputMode: 'numeric',
                // First input has no maxLength → capture raw autofill (digits + formatting), then sanitize & slice.
                maxLength: i === 0 ? undefined : 1,
              }}
              className={classes(
                [
                  styles.textInput,
                  {
                    [styles.textInputError]: error,
                  },
                ],
                {
                  ...props,
                  name: `${name}-input-item`,
                }
              )}
              key={`${startIndex}-${i}-${len}`}
              name={`${name}-field-${i}`}
              disabled={disabled || loading}
              value={value[i]}
              autoComplete={i === startIndex ? 'one-time-code' : ''}
              placeholder={translateMessage(
                {
                  id: 'media-ui-core_digitNOfTotal',
                  defaultMessage:
                    defaultMessages['media-ui-core_digitNOfTotal'],
                },
                { position: String(i + 1), total: String(length) }
              )}
              required
              onChange={onChange(i)}
            />
          );
        }

        return inputArray;
      },
      [
        value,
        disabled,
        loading,
        error,
        props,
        name,
        onKeyDown,
        onKeyUp,
        onPaste,
        onChange,
        length,
      ]
    );

    const halfClass = classes(
      [
        'input-section',
        styles.halfClass,
        {
          [styles.halfClassError]: error && variant === 'default',
          [styles.halfClassErrorDark]: error && variant === 'dark',
        },
      ],
      {
        ...props,
        name: `${name}-half`,
      }
    );

    // handle autosubmit functionality
    React.useEffect(() => {
      // if every value is set in the value state
      // and the value hasn't been submitted before
      // and autosubmit is on... run it
      if (value.every((item) => Boolean(item)) && autoSubmit && valueChanged) {
        onSubmit();
      }
    }, [value, onSubmit, valueChanged]);

    React.useEffect(() => {
      if (errorMessage.length && errorMessage !== error) {
        setError(errorMessage);
      }
    }, [errorMessage]);

    return (
      <Form
        onSubmit={onSubmit}
        noValidate
        name={name}
        autoComplete="off"
        ref={formRef}
        alignDirection="stretch"
        className={classes([], props)}
      >
        <div
          className={classes(
            [
              styles.divWrapper,

              {
                [styles.divWrapperCenter]: center,
              },
            ],
            {
              ...props,
              name: `${name}-inner`,
            }
          )}
        >
          <span
            className={classes(
              [
                halfClass,
                'input-section-first',
                {
                  'group-eight': length === 8,
                  'group-six': length === 6,
                },
              ],
              {
                ...props,
                name: `${name}-input-section-first`,
              }
            )}
          >
            {halfForm(length, true)}
          </span>
          <span className={styles.spacer} />
          <span
            className={classes(
              [
                halfClass,
                'input-section-last',
                {
                  'group-eight': length === 8,
                  'group-six': length === 6,
                },
              ],
              {
                ...props,
                name: `${name}-input-section-last`,
              }
            )}
          >
            {halfForm(length, false)}
          </span>
        </div>

        <section
          className={classes([
            styles.errorSection,

            {
              [styles.errorCenter]: center,
            },
          ])}
        >
          <div
            className={classes([
              styles.errorWrapper,

              {
                [styles.errorCenter]: center,
              },
            ])}
          >
            <ErrorText errorMsg={error} variant={variant} />
          </div>
          {buttonText ? (
            <div className={styles.marginTop}>
              <PrimaryButton
                type="submit"
                size="default"
                disabled={disabled}
                loading={loading}
              >
                {buttonText}
              </PrimaryButton>
            </div>
          ) : (
            loading && (
              <div
                className={classes([
                  styles.dots,
                  {
                    [styles.dotsWhite]: variant === 'dark',
                  },
                ])}
              >
                <LoadingDots />
              </div>
            )
          )}
        </section>
      </Form>
    );
  }
);
